import {
  ACHIEVEMENT_CATEGORIES,
  ACHIEVEMENT_LEVELS,
  type Achievement,
  type AchievementCategory,
  type AchievementLevel,
  CONSENT_KINDS,
  type ViewContext,
  shortDate,
  todayIso,
  useAchievementActions,
  useAchievementBoard,
  useAchievements,
  useClassStudents,
  useClasses,
  useConsents,
  useCourses,
  useCurrentContext,
  useSetConsent,
} from '@shkola/api';
import type { EventKind } from '@shkola/api';
import type { SubjectIcon } from '@shkola/design';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorText, Field } from '../components/ui';
import {
  Block,
  EventArt,
  GRAD,
  type Grad,
  Head,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  PAPER,
  PageHead,
  PillIcon,
  SubjectArt,
  grad,
  pillClass,
} from '../design/forms';
import { CaretDownIcon, PlusIcon, XIcon } from '../design/icons';
import { FIELD, Label } from '../student/LifeArt';
import { useMyMemberIds } from './AnnouncementsPage';

/*
 * «Досягнення» in «Форми 2» (28.10). For the family: the child's portfolio on top as soft cards in the
 * colour of the kind (olympiads lavender, sport mint, art pink, science sky, volunteering butter, the rest
 * apricot) with the kind's picture big and quiet behind — the few things the child is proud of; under it
 * the board of the class as a ruled list with a small picture each, and for a parent their consents as
 * switches. For the school: the form to add on the left (the kind as chips with pictures), what was added
 * lately as a ruled list on the right.
 */

/** Each kind of achievement: its colour and its picture (no emoji). */
const KIND: Record<AchievementCategory, { tone: Grad; subject?: SubjectIcon; event?: EventKind }> =
  {
    olympiad: { tone: 'lavender', event: 'exam' },
    sport: { tone: 'mint', subject: 'Dumbbell' },
    art: { tone: 'pink', subject: 'Palette' },
    science: { tone: 'sky', subject: 'FlaskConical' },
    volunteering: { tone: 'butter', subject: 'HeartPulse' },
    other: { tone: 'apricot', event: 'other' },
  };

/** The picture of a kind, small at a row or big and quiet behind a card (the year book too). */
export function KindArt({
  category,
  size,
  ghost,
  className,
}: {
  category: AchievementCategory;
  size: number;
  ghost?: boolean;
  className?: string;
}) {
  const kind = KIND[category] ?? KIND.other;
  return kind.subject ? (
    <SubjectArt
      icon={kind.subject}
      tone={kind.tone}
      size={size}
      ghost={ghost}
      className={className}
    />
  ) : (
    <EventArt
      kind={kind.event ?? 'other'}
      tone={kind.tone}
      size={size}
      ghost={ghost}
      className={className}
    />
  );
}

/** One of the child's own: a soft card in the kind's colour, the picture behind. */
function AchievementCard({ a }: { a: Achievement }) {
  const { t } = useTranslation();
  const tone = (KIND[a.category] ?? KIND.other).tone;
  return (
    <li
      className="relative isolate flex min-h-[200px] min-w-0 flex-col overflow-hidden rounded-[24px] p-4 md:p-5"
      style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
    >
      <KindArt
        category={a.category}
        size={150}
        ghost
        className="pointer-events-none absolute -top-4 -right-5 -z-10"
      />
      <span className="flex flex-wrap gap-1.5">
        <Label tone={tone} white>
          {t(`achievements.categories.${a.category}`)}
        </Label>
        <Label tone={tone} white>
          {t(`achievements.levels.${a.level}`)}
        </Label>
      </span>
      <span
        className="mt-auto pt-6 font-display text-[22px] leading-[27px] font-light tracking-[-0.02em]"
        style={{ color: INK }}
      >
        {a.title}
      </span>
      {a.description && <span className="mt-1.5 text-[13.5px] leading-5">{a.description}</span>}
      <span className="mt-2 text-[12.5px] opacity-80">{shortDate(a.awardedOn)}</span>
    </li>
  );
}

/** A row of a ruled list: the kind's picture, the words, what is on the right. */
function Row({
  category,
  title,
  sub,
  aside,
}: {
  category: AchievementCategory;
  title: ReactNode;
  sub: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <li className="flex min-w-0 items-center gap-3 py-3" style={{ borderTop: `1px solid ${LINE}` }}>
      <KindArt category={category} size={38} />
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] leading-[21px]" style={{ color: INK }}>
          {title}
        </span>
        <span className="mt-0.5 block text-[12.5px] leading-4" style={{ color: INK_SOFT }}>
          {sub}
        </span>
      </span>
      {aside}
    </li>
  );
}

/** A switch: on — the dark track, the knob to the right. */
function Switch({
  on,
  disabled,
  label,
  onChange,
}: {
  on: boolean;
  disabled?: boolean;
  label: string;
  onChange: (on: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!on)}
      className="relative h-[26px] w-[44px] shrink-0 rounded-full transition-colors duration-200 disabled:opacity-50"
      style={{ backgroundColor: on ? 'var(--sh-dark)' : '#E4E0D7' }}
    >
      <span
        className="absolute top-[3px] size-5 rounded-full bg-white transition-[left] duration-300 ease-spring"
        style={{ left: on ? 21 : 3 }}
      />
    </button>
  );
}

/** A guardian's own consents for the child (birthday, board, photo). */
export function ConsentsPanel({ studentId, schoolId }: { studentId: string; schoolId: string }) {
  const { t } = useTranslation();
  const mine = useMyMemberIds(schoolId);
  const consents = useConsents(studentId, mine);
  const set = useSetConsent();
  return (
    <Block>
      <Head title={t('consents.title')} />
      {!consents.data ? (
        <div
          className="h-[180px] animate-pulse rounded-[22px]"
          style={{ backgroundColor: PAPER }}
          aria-busy
        />
      ) : (
        <ul className="flex flex-col">
          {CONSENT_KINDS.map((kind) => {
            const state = consents.data[kind];
            return (
              <li
                key={kind}
                className="flex items-start gap-4 py-3.5"
                style={{ borderTop: `1px solid ${LINE}` }}
              >
                <span className="min-w-0 flex-1">
                  <span
                    className="block text-[15px] leading-[21px] font-medium"
                    style={{ color: INK }}
                  >
                    {t(`consents.kinds.${kind}`)}
                  </span>
                  <span
                    className="mt-0.5 block text-[13px] leading-[18px]"
                    style={{ color: INK_SOFT }}
                  >
                    {t(`consents.hints.${kind}`)}
                  </span>
                  {state.guardians > 1 && (
                    <span className="mt-1.5 inline-flex">
                      <Label tone={state.agreed === state.guardians ? 'mint' : 'stone'}>
                        {t('consents.agreed', { agreed: state.agreed, guardians: state.guardians })}
                      </Label>
                    </span>
                  )}
                </span>
                <Switch
                  on={state.mine}
                  disabled={set.isPending}
                  label={t(`consents.kinds.${kind}`)}
                  onChange={(granted) => set.mutate({ studentId, kind, granted })}
                />
              </li>
            );
          })}
        </ul>
      )}
      <p className="mt-2 text-[13px] leading-[18px]" style={{ color: INK_FAINT }}>
        {t('consents.hint')}
      </p>
    </Block>
  );
}

function FamilyView({ ctx }: { ctx: Extract<ViewContext, { kind: 'student' | 'guardian' }> }) {
  const { t } = useTranslation();
  const portfolio = useAchievements({ studentId: ctx.child.memberId });
  const board = useAchievementBoard(ctx.schoolId);
  const classBoard = (board.data ?? []).filter((b) => b.classId === ctx.child.class?.id);
  const mine = portfolio.data ?? [];
  const guardian = ctx.kind === 'guardian';
  return (
    <>
      <Block>
        <Head
          title={`${t('achievements.portfolio')} · ${ctx.child.firstName}`}
          count={mine.length}
        />
        {portfolio.isPending ? (
          <div
            className="h-[200px] animate-pulse rounded-[24px]"
            style={{ backgroundColor: PAPER }}
            aria-busy
          />
        ) : mine.length === 0 ? (
          <div className="flex flex-col items-center py-10 text-center">
            <EventArt kind="exam" tone="lavender" size={88} />
            <p className="mt-3 font-display text-[24px] font-light" style={{ color: INK }}>
              {t('achievements.empty')}
            </p>
          </div>
        ) : (
          <ul className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
            {mine.map((a) => (
              <AchievementCard key={a.id} a={a} />
            ))}
          </ul>
        )}
      </Block>
      <div
        className={`grid grid-cols-1 items-start gap-4 ${guardian ? 'lg:grid-cols-[minmax(0,1fr)_400px]' : ''}`}
      >
        <Block>
          <Head title={`${t('achievements.board')} ${ctx.child.class?.label ?? ''}`} />
          {board.isPending ? (
            <div
              className="h-[240px] animate-pulse rounded-[22px]"
              style={{ backgroundColor: PAPER }}
              aria-busy
            />
          ) : classBoard.length === 0 ? (
            <p className="py-6 text-[15px]" style={{ color: INK_SOFT }}>
              {t('achievements.boardEmpty')}
            </p>
          ) : (
            <ul className={`grid grid-cols-1 gap-x-8 ${guardian ? '' : 'xl:grid-cols-2'}`}>
              {classBoard.map((b) => (
                <Row
                  key={b.id}
                  category={b.category}
                  title={
                    <>
                      <span className="font-medium">{b.name}</span> — {b.title}
                    </>
                  }
                  sub={`${shortDate(b.awardedOn)} · ${t(`achievements.levels.${b.level}`)}`}
                />
              ))}
            </ul>
          )}
          <p className="mt-3 text-[13px] leading-[18px]" style={{ color: INK_FAINT }}>
            {t('achievements.boardHint')}
          </p>
        </Block>
        {guardian && (
          <div className="lg:sticky lg:top-20">
            <ConsentsPanel studentId={ctx.child.memberId} schoolId={ctx.schoolId} />
          </div>
        )}
      </div>
    </>
  );
}

function AddAchievement({ ctx }: { ctx: Extract<ViewContext, { kind: 'teacher' | 'admin' }> }) {
  const { t } = useTranslation();
  const allClasses = useClasses(ctx.schoolId);
  const courses = useCourses({ teacherMemberId: ctx.kind === 'teacher' ? ctx.memberId : '' });
  // a teacher adds for the classes they teach or lead, the administration for any
  const mine = new Set([
    ...(courses.data ?? []).map((c) => c.classId),
    ...(ctx.kind === 'teacher' && ctx.homeroomClass ? [ctx.homeroomClass.id] : []),
  ]);
  const classes = (allClasses.data ?? []).filter((c) => ctx.kind === 'admin' || mine.has(c.id));
  const [classId, setClassId] = useState(
    ctx.kind === 'teacher' ? (ctx.homeroomClass?.id ?? '') : '',
  );
  const klass = classId || classes[0]?.id;
  const students = useClassStudents(klass);
  const [studentId, setStudentId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<AchievementCategory>('olympiad');
  const [level, setLevel] = useState<AchievementLevel>('school');
  const [awardedOn, setAwardedOn] = useState(todayIso());
  const [error, setError] = useState<string>();
  const actions = useAchievementActions();
  const student = studentId || students.data?.[0]?.id;
  const label = 'mb-1.5 block text-[13px] font-medium text-ink-soft';

  return (
    <Block className="lg:sticky lg:top-20">
      <Head title={t('achievements.add')} />
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!student) return;
          setError(undefined);
          actions.add
            .mutateAsync({
              schoolId: ctx.schoolId,
              studentId: student,
              addedBy: ctx.memberId,
              category,
              level,
              title,
              description,
              awardedOn,
            })
            .then(
              () => {
                setTitle('');
                setDescription('');
              },
              () => setError(t('common.error')),
            );
        }}
      >
        <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,5fr)] gap-2">
          <label className="block min-w-0">
            <span className={label}>{t('achievements.class')}</span>
            <select
              className={`h-11 w-full px-3 ${FIELD}`}
              value={klass ?? ''}
              onChange={(e) => {
                setClassId(e.target.value);
                setStudentId('');
              }}
            >
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block min-w-0">
            <span className={label}>{t('achievements.student')}</span>
            <select
              className={`h-11 w-full px-3 ${FIELD}`}
              value={student ?? ''}
              onChange={(e) => setStudentId(e.target.value)}
            >
              {(students.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <Field
          id="achievement-title"
          label={t('achievements.titleLabel')}
          placeholder={t('achievements.titlePlaceholder')}
          required
          maxLength={200}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <Field
          id="achievement-description"
          label={t('achievements.description')}
          maxLength={1000}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        {/* the kind: chips with its picture, as the subjects in «Домашні» */}
        <div role="radiogroup" aria-label={t('achievements.category')}>
          <span className={label}>{t('achievements.category')}</span>
          <div className="flex flex-wrap gap-1.5">
            {ACHIEVEMENT_CATEGORIES.map((c) => {
              const on = c === category;
              return (
                <button
                  key={c}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => setCategory(c)}
                  className={`inline-flex h-10 items-center gap-2 rounded-full pr-3.5 pl-1.5 text-[14px] transition-colors ${on ? 'font-medium' : 'hover:bg-[#EFECE5]'}`}
                  style={{ backgroundColor: on ? '#E9E5DC' : PAPER, color: INK }}
                >
                  <KindArt category={c} size={28} />
                  {t(`achievements.categories.${c}`)}
                </button>
              );
            })}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="block min-w-0">
            <span className={label}>{t('achievements.level')}</span>
            <select
              className={`h-11 w-full px-3 ${FIELD}`}
              value={level}
              onChange={(e) => setLevel(e.target.value as AchievementLevel)}
            >
              {ACHIEVEMENT_LEVELS.map((l) => (
                <option key={l} value={l}>
                  {t(`achievements.levels.${l}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="block min-w-0">
            <span className={label}>{t('achievements.date')}</span>
            <input
              type="date"
              max={todayIso()}
              value={awardedOn}
              onChange={(e) => setAwardedOn(e.target.value)}
              className={`h-11 w-full px-3 ${FIELD}`}
            />
          </label>
        </div>
        <ErrorText>{error}</ErrorText>
        <div>
          <button
            type="submit"
            disabled={actions.add.isPending || !title.trim() || !student}
            className={`${pillClass('dark')} disabled:opacity-35`}
          >
            {t('achievements.add')}
            <PillIcon icon={PlusIcon} />
          </button>
        </div>
      </form>
    </Block>
  );
}

function StaffView({ ctx }: { ctx: Extract<ViewContext, { kind: 'teacher' | 'admin' }> }) {
  const { t } = useTranslation();
  const recent = useAchievements({ schoolId: ctx.schoolId });
  const actions = useAchievementActions();
  const [all, setAll] = useState(false);
  const latest = (recent.data ?? []).slice(0, 30);
  const list = all ? latest : latest.slice(0, 12);
  return (
    <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[440px_minmax(0,1fr)]">
      <AddAchievement ctx={ctx} />
      <Block>
        <Head title={t('achievements.recent')} />
        {recent.isPending ? (
          <div
            className="h-[320px] animate-pulse rounded-[22px]"
            style={{ backgroundColor: PAPER }}
            aria-busy
          />
        ) : list.length === 0 ? (
          <div className="flex flex-col items-center py-10 text-center">
            <EventArt kind="exam" tone="lavender" size={88} />
            <p className="mt-3 font-display text-[24px] font-light" style={{ color: INK }}>
              {t('achievements.empty')}
            </p>
          </div>
        ) : (
          <ul className="flex flex-col">
            {list.map((a) => (
              <Row
                key={a.id}
                category={a.category}
                title={a.title}
                sub={[
                  a.studentName,
                  shortDate(a.awardedOn),
                  t(`achievements.categories.${a.category}`),
                  t(`achievements.levels.${a.level}`),
                ]
                  .filter(Boolean)
                  .join(' · ')}
                aside={
                  (ctx.kind === 'admin' || a.addedBy === ctx.memberId) && (
                    <button
                      type="button"
                      aria-label={t('achievements.remove')}
                      title={t('achievements.remove')}
                      onClick={() => actions.remove.mutate(a.id)}
                      className="inline-flex size-8 shrink-0 items-center justify-center rounded-full ring-1 ring-[#E4E0D7] ring-inset transition-colors hover:bg-[#F5F3EF]"
                      style={{ color: INK_SOFT }}
                    >
                      <XIcon size={13} weight="bold" aria-hidden />
                    </button>
                  )
                }
              />
            ))}
          </ul>
        )}
        {!all && latest.length > list.length && (
          <button
            type="button"
            onClick={() => setAll(true)}
            className="mt-2 flex items-center gap-2 self-start py-1 text-[14px] font-medium"
            style={{ color: INK }}
          >
            {t('rewards.showAll')}
            <span className="text-[13px] font-normal" style={{ color: INK_FAINT }}>
              {latest.length - list.length}
            </span>
            <CaretDownIcon size={12} weight="bold" aria-hidden />
          </button>
        )}
      </Block>
    </div>
  );
}

export function AchievementsPage() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  return (
    <div className="flex flex-col gap-4">
      <PageHead title={t('achievements.title')} />
      {ctx.kind === 'student' || ctx.kind === 'guardian' ? (
        <FamilyView ctx={ctx} />
      ) : ctx.kind === 'psychologist' ? null : (
        <StaffView ctx={ctx} />
      )}
    </div>
  );
}
